import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import { getAllAppointments } from "../apis/Appointment";
import { toast } from "react-toastify";

const Dashboard = () => {
  const [timeFrame, setTimeFrame] = useState("monthly");
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await getAllAppointments(1, 1000, "All", "dashboard");
      setAppointments(response.data);
    } catch (error) {
      toast.error("Failed to fetch appointments");
      console.error("Error fetching appointments:", error);
    } finally {
      setLoading(false);
    }
  };

  // Process data for charts
  const processData = () => {
    const currentDate = new Date();
    const data = [];

    if (timeFrame === "monthly") {
      // Get last 12 months
      for (let i = 11; i >= 0; i--) {
        const date = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() - i,
          1
        );
        const monthName = date.toLocaleString("default", { month: "short" });
        const monthAppointments = appointments.filter((apt) => {
          const aptDate = new Date(apt.date);
          return (
            aptDate.getMonth() === date.getMonth() &&
            aptDate.getFullYear() === date.getFullYear()
          );
        });

        const paidCount = monthAppointments.filter(
          (apt) => apt.status === "Paid"
        ).length;
        const pendingCount = monthAppointments.filter(
          (apt) => apt.status === "Pending"
        ).length;

        data.push({
          name: monthName,
          total: monthAppointments.length,
          paid: paidCount,
          pending: pendingCount,
          amount: paidCount * 500,
          percentage:
            monthAppointments.length > 0
              ? (paidCount / monthAppointments.length) * 100
              : 0,
        });
      }
    } else {
      // Get last 5 years
      for (let i = 4; i >= 0; i--) {
        const year = currentDate.getFullYear() - i;
        const yearAppointments = appointments.filter((apt) => {
          const aptDate = new Date(apt.date);
          return aptDate.getFullYear() === year;
        });

        const paidCount = yearAppointments.filter(
          (apt) => apt.status === "Paid"
        ).length;
        const pendingCount = yearAppointments.filter(
          (apt) => apt.status === "Pending"
        ).length;

        data.push({
          name: year.toString(),
          total: yearAppointments.length,
          paid: paidCount,
          pending: pendingCount,
          amount: paidCount * 500,
          percentage:
            yearAppointments.length > 0
              ? (paidCount / yearAppointments.length) * 100
              : 0,
        });
      }
    }

    return data;
  };

  const chartData = processData();

  const commonProps = {
    data: chartData,
    margin: { top: 20, right: 30, left: 20, bottom: 5 },
  };

  const renderTotalPatientsChart = () => (
    <BarChart {...commonProps}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="name" />
      <YAxis />
      <Tooltip
        formatter={(value) => [`${value} patients`, "Total Patients"]}
        labelFormatter={(label) =>
          `${timeFrame === "monthly" ? "Month" : "Year"}: ${label}`
        }
      />
      <Legend />
      <Bar dataKey="total" name="Total Patients" fill="#12b48b" />
    </BarChart>
  );

  const renderPaidPatientsChart = () => (
    <BarChart {...commonProps}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="name" />
      <YAxis />
      <Tooltip
        formatter={(value) => [`${value} patients`, "Paid Patients"]}
        labelFormatter={(label) =>
          `${timeFrame === "monthly" ? "Month" : "Year"}: ${label}`
        }
      />
      <Legend />
      <Bar dataKey="paid" name="Paid Patients" fill="#4caf50" />
    </BarChart>
  );

  const renderUnpaidPatientsChart = () => (
    <BarChart {...commonProps}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="name" />
      <YAxis />
      <Tooltip
        formatter={(value) => [`${value} patients`, "Unpaid Patients"]}
        labelFormatter={(label) =>
          `${timeFrame === "monthly" ? "Month" : "Year"}: ${label}`
        }
      />
      <Legend />
      <Bar dataKey="pending" name="Unpaid Patients" fill="#ff9800" />
    </BarChart>
  );

  const renderRevenueChart = () => (
    <LineChart {...commonProps}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="name" />
      <YAxis />
      <Tooltip
        formatter={(value) => [`₹${value}`, "Revenue"]}
        labelFormatter={(label) =>
          `${timeFrame === "monthly" ? "Month" : "Year"}: ${label}`
        }
      />
      <Legend />
      <Line
        type="monotone"
        dataKey="amount"
        name="Revenue"
        stroke="#12b48b"
        strokeWidth={2}
      />
    </LineChart>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <ToggleButtonGroup
          value={timeFrame}
          exclusive
          onChange={(e, newValue) => newValue && setTimeFrame(newValue)}
        >
          <ToggleButton value="monthly">Monthly</ToggleButton>
          <ToggleButton value="yearly">Yearly</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {/* First Row */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              width: "100%",
              marginBottom: "2rem",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Paper sx={{ height: 350 }}>
                <Box
                  sx={{
                    p: 2,
                    mb: 3,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="h6">Revenue Trend</Typography>
                </Box>
                <Box sx={{ p: 2, height: "calc(100% - 75px)" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    {renderRevenueChart()}
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Paper sx={{ height: 350 }}>
                <Box
                  sx={{
                    p: 2,
                    mb: 3,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="h6">Total Patients Trend</Typography>
                </Box>
                <Box sx={{ p: 2, height: "calc(100% - 75px)" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    {renderTotalPatientsChart()}
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Box>
          </Box>

          {/* Second Row */}
          <Box sx={{ display: "flex", gap: 3, width: "100%" }}>
            <Box sx={{ flex: 1 }}>
              <Paper sx={{ height: 350 }}>
                <Box
                  sx={{
                    p: 2,
                    mb: 3,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="h6">Paid Patients Trend</Typography>
                </Box>
                <Box sx={{ p: 2, height: "calc(100% - 75px)" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    {renderPaidPatientsChart()}
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Paper sx={{ height: 350 }}>
                <Box
                  sx={{
                    p: 2,
                    mb: 3,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="h6">Unpaid Patients Trend</Typography>
                </Box>
                <Box sx={{ p: 2, height: "calc(100% - 75px)" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    {renderUnpaidPatientsChart()}
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
